import { useEffect, useState } from "react";

/** Re-renders every `intervalMs` so ages ("2 min temu") stay honest. */
export function useNow(intervalMs = 5000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Seconds between `iso` and `now`; null when unknown. */
export function ageSeconds(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null;
  return Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
}

/** Short Polish-friendly age: "teraz", "45 s", "3 min", "2 h 5 min". */
export function formatAge(seconds: number | null): string {
  if (seconds === null) return "—";
  if (seconds < 10) return "teraz";
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${minutes % 60} min`;
}

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });
}

/** "11.10, 14:30": a time that may be tomorrow (validity of a link) needs its day. */
export function formatDayTime(iso: string): string {
  return new Date(iso).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** A position older than this is drawn grey and marked stale. */
export const STALE_POSITION_S = 120;

/**
 * Coverage percentage for display. Below 10% one decimal place, so a sector someone has
 * started never reads as an untouched "0%".
 */
export function formatPct(pct: number): string {
  if (pct > 0 && pct < 0.1) return "<0,1%";
  return `${pct.toLocaleString("pl-PL", { maximumFractionDigits: pct < 10 ? 1 : 0 })}%`;
}

/**
 * Value for <input type="datetime-local">, which shows the browser's local time.
 * Never slice the ISO string: that shows UTC, and saving it back as local time shifts the
 * moment by the zone offset on every save.
 */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Back from datetime-local (local time, no zone) to a UTC ISO instant. */
export function fromLocalInput(value: string): string | null {
  return value ? new Date(value).toISOString() : null;
}
